import React from 'react';
import { Alta, DecisionRevisar, Ejecutiva, EstadoAlta, OrdenAltas, RevisionEnCurso } from '../../types/Altas';
import { Badge, Button, Panel } from '../ui';
import { PencilIcon, SearchIcon, SpinnerIcon, TrashIcon } from '../icons';
import { ESTADO_UI, ESTADOS, filtroClass, formatFecha, formatFechaSqlite, inputClass, textoRevisar, thClass } from './altasUi';
import Paginador from './Paginador';

interface Props {
  altas: Alta[];
  ejecutivas: Ejecutiva[];
  filtroEjecutiva: string;
  filtroEstado: string;
  onFiltroEjecutiva: (v: string) => void;
  onFiltroEstado: (v: string) => void;
  busqueda: string;
  onBusqueda: (v: string) => void;
  /** Lo que de verdad se buscó (la búsqueda espera a que se deje de tipear). */
  busquedaAplicada: string;
  onEditar: (alta: Alta) => void;
  onBorrar: (alta: Alta) => void;
  /** El jefe resuelve una venta marcada para revisar. */
  onRevisar: (alta: Alta, decision: DecisionRevisar) => void;
  /** Alta y decisión en camino; mientras tanto no se decide otra. */
  revisando?: RevisionEnCurso | null;
  orden: OrdenAltas;
  onOrden: (v: OrdenAltas) => void;
  total: number;
  pagina: number;
  porPagina: number;
  onPagina: (p: number) => void;
}


const AltasTable: React.FC<Props> = ({
  altas,
  ejecutivas,
  filtroEjecutiva,
  filtroEstado,
  onFiltroEjecutiva,
  onFiltroEstado,
  busqueda,
  onBusqueda,
  busquedaAplicada,
  onEditar,
  onBorrar,
  onRevisar,
  revisando = null,
  orden,
  onOrden,
  total,
  pagina,
  porPagina,
  onPagina,
}) => {
  const paginas = Math.max(1, Math.ceil(total / porPagina));
  const desde = total === 0 ? 0 : (pagina - 1) * porPagina + 1;
  const hasta = Math.min(pagina * porPagina, total);

  return (
    <Panel
      title="Altas cargadas"
      subtitle={`${total} ${total === 1 ? 'alta' : 'altas'}${total > porPagina ? ` · mostrando ${desde} a ${hasta}` : ''}`}
      actions={
        <>
          <span className="relative shrink-0">
            <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={busqueda}
              onChange={(e) => onBusqueda(e.target.value)}
              placeholder="Nombre o DNI"
              aria-label="Buscar alta"
              className={`${inputClass} !w-56 py-1.5 ps-8`}
            />
          </span>
          <select
            value={orden}
            onChange={(e) => onOrden(e.target.value as OrdenAltas)}
            aria-label="Ordenar"
            className={filtroClass}
          >
            <option value="venta_desc">Venta más reciente primero</option>
            <option value="venta_asc">Venta más antigua primero</option>
            <option value="carga_desc">Carga más reciente primero</option>
            <option value="carga_asc">Carga más antigua primero</option>
          </select>
          <select
            value={filtroEjecutiva}
            onChange={(e) => onFiltroEjecutiva(e.target.value)}
            aria-label="Filtrar por ejecutivo"
            className={filtroClass}
          >
            <option value="">Todos los ejecutivos</option>
            {ejecutivas.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nombre}
              </option>
            ))}
          </select>
          <select
            value={filtroEstado}
            onChange={(e) => onFiltroEstado(e.target.value)}
            aria-label="Filtrar por estado"
            className={filtroClass}
          >
            <option value="">Todos los estados</option>
            {ESTADOS.map((s: EstadoAlta) => (
              <option key={s} value={s}>
                {ESTADO_UI[s].label}
              </option>
            ))}
            <option value="revisar">Para revisar</option>
          </select>
        </>
      }
    >
      {altas.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-400">
          {busquedaAplicada
            ? `Nada que coincida con "${busquedaAplicada}".`
            : 'Todavía no hay altas cargadas con estos filtros.'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left">
                <th className={`${thClass} pe-4`}>Ejecutivo</th>
                <th className={`${thClass} pe-4`}>DNI</th>
                <th className={`${thClass} pe-4`}>Nombre</th>
                <th className={`${thClass} pe-4`}>Plan cargado</th>
                <th className={`${thClass} pe-4`}>Estado</th>
                <th className={`${thClass} pe-4`}>Plan en Salesforce</th>
                <th className={`${thClass} pe-4`}>Fecha de venta</th>
                <th className={`${thClass} pe-4`}>Cargada</th>
                <th className={thClass} />
              </tr>
            </thead>
            <tbody>
              {altas.map((a) => {
                const estado = ESTADO_UI[a.estado];
                const revisar = textoRevisar(a);
                return (
                  <React.Fragment key={a.id}>
                  <tr
                    className={`transition-colors duration-100 ease-out hover:bg-slate-50/70 ${
                      revisar ? 'bg-amber-50/40' : 'border-b border-slate-100 last:border-0'
                    }`}
                  >
                    <td className="py-2 pe-4 font-medium text-gappex-secondary">{a.ejecutiva}</td>
                    <td className="py-2 pe-4 tabular-nums text-slate-700">{a.dni}</td>
                    <td className="py-2 pe-4 text-slate-700">{a.nombre ?? '—'}</td>
                    <td className="py-2 pe-4 text-slate-700">{a.planCargado}</td>
                    <td className="py-2 pe-4">
                      <Badge tone={estado.tone} title={a.syncError ?? estado.title}>
                        {estado.label}
                      </Badge>
                      {revisar && (
                        <Badge tone="warning" className="ms-1.5" title="Hay algo para mirar antes de comisionarla">
                          Revisar
                        </Badge>
                      )}
                    </td>
                    <td className="py-2 pe-4 text-slate-700">
                      {a.planSf ?? '—'}
                      {(a.cantidadPlanes ?? 1) > 1 && (
                        <span
                          className="ms-1.5 tabular-nums text-slate-400"
                          title={`Plan para ${a.cantidadPlanes} miembros: cuenta como ${a.cantidadPlanes} ventas`}
                        >
                          × {a.cantidadPlanes}
                        </span>
                      )}
                    </td>
                    <td className="py-2 pe-4 tabular-nums text-slate-700">
                      {formatFecha(a.fechaVenta)}
                    </td>
                    <td
                      className="py-2 pe-4 tabular-nums text-slate-400"
                      title={`Por ${a.createdBy}`}
                    >
                      {formatFechaSqlite(a.createdAt)}
                    </td>
                    <td className="py-1 text-right whitespace-nowrap">
                      <Button
                        variant="ghost"
                        iconOnly
                        aria-label="Corregir"
                        title="Corregir ejecutivo, nombre o plan"
                        onClick={() => onEditar(a)}
                      >
                        <PencilIcon />
                      </Button>
                      <Button
                        variant="ghost"
                        iconOnly
                        aria-label="Borrar"
                        title="Borrar el alta"
                        className="hover:text-red-600"
                        onClick={() => onBorrar(a)}
                      >
                        <TrashIcon />
                      </Button>
                    </td>
                  </tr>
                  {revisar && (
                    <tr className="border-b border-slate-100 bg-amber-50/40 last:border-0">
                      <td colSpan={9} className="px-3 pb-2.5 pt-0.5 text-xs text-amber-800">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                          <span>{revisar}</span>
                          <span className="flex gap-1.5">
                            <Button
                              type="button"
                              variant="secondary"
                              className="!py-1 !text-xs"
                              title="Es una venta del ejecutivo: se comisiona"
                              disabled={revisando !== null}
                              icon={revisando?.id === a.id && revisando.decision === 'venta' ? <SpinnerIcon /> : undefined}
                              onClick={() => onRevisar(a, 'venta')}
                            >
                              Es venta
                            </Button>
                            <Button
                              type="button"
                              variant="secondary"
                              className="!py-1 !text-xs"
                              title='Ya tenía el plan: pasa a "Ya era cliente" y no se comisiona'
                              disabled={revisando !== null}
                              icon={revisando?.id === a.id && revisando.decision === 'ya_tenia_plan' ? <SpinnerIcon /> : undefined}
                              onClick={() => onRevisar(a, 'ya_tenia_plan')}
                            >
                              Ya tenía plan
                            </Button>
                          </span>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Paginador pagina={pagina} paginas={paginas} onPagina={onPagina} />
    </Panel>
  );
};

export default AltasTable;
